import { useState, useCallback, useEffect, useRef, useId } from 'react'
import { Outlet, useLocation, Link } from 'react-router-dom'
import { LogOut, ChevronDown, ChevronRight, Crown, Eye, Menu, KeyRound, Building2 } from 'lucide-react'
import { MotionConfig } from 'framer-motion'
import { cn } from '@/lib/utils'
import Sidebar, { getRouteContext } from './Sidebar.jsx'
import PreferencesMenu from '@/components/PreferencesMenu.jsx'
import TenantSwitcher from '@/components/TenantSwitcher.jsx'
import { useAuth } from '@/data/auth'
import { usePrefs } from '@/data/preferences'

export default function AppLayout() {
  const { prefs } = usePrefs()
  const [sidebarCollapsed, setSidebarCollapsed] = useState(prefs.sidebarDefault === 'collapsed')
  const [mobileOpen, setMobileOpen] = useState(false)
  const location = useLocation()
  const route = getRouteContext(location.pathname)
  const toggleSidebar = useCallback(() => setSidebarCollapsed((previous) => !previous), [])
  const closeMobile = useCallback(() => setMobileOpen(false), [])

  useEffect(() => { setSidebarCollapsed(prefs.sidebarDefault === 'collapsed') }, [prefs.sidebarDefault])

  return (
    <MotionConfig reducedMotion={prefs.reducedMotion ? 'always' : 'user'}>
      <div className="workspace-shell">
        <a className="skip-to-content" href="#workspace-main">Skip to main content</a>
        <Sidebar collapsed={sidebarCollapsed} onToggle={toggleSidebar} mobileOpen={mobileOpen} onMobileClose={closeMobile} />
        <div className={cn('workspace-content', sidebarCollapsed && 'sidebar-is-collapsed')} inert={mobileOpen ? true : undefined}>
          <header className="workspace-topbar">
            <div className="workspace-topbar-context">
              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                className="shell-icon-button mobile-menu-toggle"
                aria-label="Open navigation"
                aria-expanded={mobileOpen}
                aria-controls={mobileOpen ? 'workspace-mobile-navigation' : undefined}
              ><Menu size={20} /></button>
              <Building2 className="workspace-context-icon" size={16} aria-hidden="true" />
              <div className="workspace-breadcrumb" aria-label="Current section">
                <span>{route.section === route.title ? 'Workspace' : route.section}</span>
                <ChevronRight size={13} aria-hidden="true" />
                <span>{route.title}</span>
              </div>
            </div>
            <div className="workspace-topbar-actions">
              <div className="workspace-desktop-actions"><TenantSwitcher /></div>
              <div className="workspace-mobile-actions"><TenantSwitcher compact /></div>
              <PreferencesMenu compact />
              <UserMenu />
            </div>
          </header>
          <main id="workspace-main" className="workspace-main" tabIndex={-1}>
            <Outlet />
          </main>
        </div>
      </div>
    </MotionConfig>
  )
}

function UserMenu() {
  const { user, signOut } = useAuth()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const triggerRef = useRef(null)
  const menuId = useId()
  const location = useLocation()

  useEffect(() => { setOpen(false) }, [location.pathname])
  useEffect(() => {
    if (!open) return
    const onClick = (event) => { if (!ref.current?.contains(event.target)) setOpen(false) }
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    document.addEventListener('pointerdown', onClick)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onClick)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  if (!user) return null
  const RoleIcon = user.role === 'admin' ? Crown : Eye
  const initials = (user.name || user.email).split(/\s+/).map((part) => part[0]).slice(0, 2).join('').toUpperCase()

  return (
    <div ref={ref} className="relative" onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false) }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((previous) => !previous)}
        className="workspace-user-trigger"
        aria-label={`Account menu for ${user.name || user.email}`}
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
      >
        {user.picture ? <img src={user.picture} alt="" className="workspace-avatar" /> : <span className="workspace-avatar">{initials}</span>}
        <span className="workspace-user-name">{user.name || user.email.split('@')[0]}</span>
        <ChevronDown size={13} aria-hidden="true" />
      </button>
      {open && (
        <div id={menuId} className="workspace-account-menu pp-menu">
          <div className="workspace-account-details">
            <strong>{user.name || user.email}</strong>
            <span>{user.email}</span>
            <span className="workspace-role"><RoleIcon size={11} aria-hidden="true" />{user.role}</span>
          </div>
          <Link to="/change-password" onClick={() => setOpen(false)}><KeyRound size={15} aria-hidden="true" />{user.has_password ? 'Change password' : 'Set password'}</Link>
          <button type="button" onClick={signOut}><LogOut size={15} aria-hidden="true" />Sign out</button>
        </div>
      )}
    </div>
  )
}
